import React, { useState, useEffect } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Select from "../../../components/ui/Select"
import Button from "../../../components/ui/Button"
import ReauthModal from "../../../components/ui/ReauthModal"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { listSegments } from "../../../utils/segmentHelpers"
import {
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  listAnnouncements,
  publishAnnouncement,
  publishDueScheduledAnnouncements,
} from "../../../utils/announcementHelpers"

const AUDIENCE_OPTIONS = [
  { value: "all", label: "All Learners" },
  { value: "segment", label: "Saved Segment" },
]

const STATUS_BADGE = {
  draft: { label: "Draft", variant: "secondary" },
  scheduled: { label: "Scheduled", variant: "outline" },
  published: { label: "Published", variant: "default" },
}

const emptyForm = {
  title: "",
  body: "",
  ctaLabel: "",
  ctaUrl: "",
  audienceType: "all",
  segmentId: "",
  scheduledFor: "",
}

const AnnouncementsPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [announcements, setAnnouncements] = useState([])
  const [segments, setSegments] = useState([])
  const [loading, setLoading] = useState(true)

  const [showBuilder, setShowBuilder] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState("")

  const [pendingDeleteId, setPendingDeleteId] = useState(null)
  const [publishingId, setPublishingId] = useState(null)

  const loadAll = async () => {
    setLoading(true)
    // Self-heal: fire off any scheduled announcements whose time has
    // already passed before rendering the list.
    await publishDueScheduledAnnouncements(orgId)
    const [{ announcements: list }, { segments: segmentList }] = await Promise.all([
      listAnnouncements(orgId),
      listSegments(orgId),
    ])
    setAnnouncements(list || [])
    setSegments(segmentList || [])
    setLoading(false)
  }

  useEffect(() => {
    if (orgId) loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const openNewBuilder = () => {
    setEditingId(null)
    setForm(emptyForm)
    setFormError("")
    setShowBuilder(true)
  }

  const openEditBuilder = (a) => {
    setEditingId(a.announcementId)
    setForm({
      title: a.title || "",
      body: a.body || "",
      ctaLabel: a.ctaLabel || "",
      ctaUrl: a.ctaUrl || "",
      audienceType: a.audienceType || "all",
      segmentId: a.segmentId || "",
      scheduledFor: a.scheduledFor
        ? new Date(a.scheduledFor?.toDate ? a.scheduledFor.toDate() : a.scheduledFor).toISOString().slice(0, 16)
        : "",
    })
    setFormError("")
    setShowBuilder(true)
  }

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e?.target?.value }))

  const handleSave = async (publishNow = false) => {
    if (!form.title?.trim() || !form.body?.trim()) {
      setFormError("Title and message body are required")
      return
    }
    if (form.audienceType === "segment" && !form.segmentId) {
      setFormError("Select a segment to target")
      return
    }
    setSaving(true)

    const scheduledFor = form.scheduledFor ? new Date(form.scheduledFor) : null
    const payload = {
      orgId,
      title: form.title,
      body: form.body,
      ctaLabel: form.ctaLabel,
      ctaUrl: form.ctaUrl,
      audienceType: form.audienceType,
      segmentId: form.audienceType === "segment" ? form.segmentId : null,
      scheduledFor,
    }

    const { announcementId, error } = editingId
      ? await updateAnnouncement(orgId, editingId, payload).then((r) => ({ announcementId: editingId, ...r }))
      : await createAnnouncement(payload)

    if (error) {
      setSaving(false)
      setFormError(error?.message || "Could not save announcement")
      return
    }

    if (publishNow && announcementId) {
      await publishAnnouncement(orgId, announcementId, form.audienceType, form.segmentId)
    }

    setSaving(false)
    setShowBuilder(false)
    loadAll()
  }

  const handlePublishNow = async (a) => {
    setPublishingId(a.announcementId)
    await publishAnnouncement(orgId, a.announcementId, a.audienceType, a.segmentId)
    setPublishingId(null)
    loadAll()
  }

  const handleDelete = async () => {
    await deleteAnnouncement(orgId, pendingDeleteId)
    setPendingDeleteId(null)
    loadAll()
  }

  const audienceLabel = (a) => {
    if (a.audienceType === "segment") {
      const segment = segments?.find((s) => s?.segmentId === a.segmentId)
      return segment ? `Segment: ${segment.name}` : "Segment (deleted)"
    }
    return "All Learners"
  }

  return (
    <div className="space-y-6 md:col-span-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {announcements?.length || 0} announcement{announcements?.length === 1 ? "" : "s"}
        </p>
        <Button variant="default" iconName="Plus" iconPosition="left" onClick={openNewBuilder}>
          New Announcement
        </Button>
      </div>

      {showBuilder && (
        <Card className="dark:bg-transparent">
          <CardContent className="pt-5 space-y-4">
            <Input label="Title" placeholder="e.g. New capstone deadline extended" value={form.title} onChange={handleChange("title")} />
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Message</label>
              <textarea
                rows={4}
                value={form.body}
                onChange={handleChange("body")}
                placeholder="What do you want learners to know?"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Plain text for now — no rich-text editor is wired into the app yet.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="CTA label (optional)" placeholder="e.g. View Deadline" value={form.ctaLabel} onChange={handleChange("ctaLabel")} />
              <Input label="CTA link (optional)" placeholder="https://..." value={form.ctaUrl} onChange={handleChange("ctaUrl")} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Audience</label>
                <Select
                  options={AUDIENCE_OPTIONS}
                  value={form.audienceType}
                  onChange={(v) => setForm((prev) => ({ ...prev, audienceType: v, segmentId: "" }))}
                />
              </div>
              {form.audienceType === "segment" && (
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Segment</label>
                  <Select
                    options={[
                      { value: "", label: segments?.length ? "Select a segment..." : "No saved segments yet" },
                      ...(segments?.map((s) => ({ value: s.segmentId, label: s.name })) || []),
                    ]}
                    value={form.segmentId}
                    onChange={(v) => setForm((prev) => ({ ...prev, segmentId: v }))}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Schedule for later (optional)</label>
              <Input type="datetime-local" value={form.scheduledFor} onChange={handleChange("scheduledFor")} className="w-64" />
              <p className="text-xs text-muted-foreground mt-1">
                There's no background scheduler yet — a scheduled announcement goes out the next time this panel is
                opened after that time, not automatically in the background.
              </p>
            </div>

            {formError && <p className="text-sm text-destructive">{formError}</p>}

            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => setShowBuilder(false)}>
                Cancel
              </Button>
              <Button variant="outline" loading={saving} onClick={() => handleSave(false)}>
                {form.scheduledFor ? "Save Scheduled" : "Save as Draft"}
              </Button>
              <Button variant="default" loading={saving} onClick={() => handleSave(true)}>
                Publish Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="p-12 text-center text-muted-foreground text-sm">Loading announcements...</div>
      ) : announcements?.length === 0 ? (
        <Card className="p-12 text-center dark:bg-transparent">
          <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Megaphone" size={24} className="text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">No announcements yet</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Send a targeted message to all learners or a saved segment.
          </p>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={openNewBuilder}>
            New Announcement
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {announcements?.map((a) => {
            const statusMeta = STATUS_BADGE[a.status] || STATUS_BADGE.draft
            return (
              <Card key={a.announcementId} className="dark:bg-transparent">
                <CardContent className="pt-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-foreground text-sm">{a.title}</h4>
                        <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.body}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                    <span>{audienceLabel(a)}</span>
                    {a.status === "published" && <span>· Reached {a.audienceCount ?? 0} learners</span>}
                    {a.status === "scheduled" && a.scheduledFor && (
                      <span>
                        · Scheduled for{" "}
                        {new Date(a.scheduledFor?.toDate ? a.scheduledFor.toDate() : a.scheduledFor).toLocaleString()}
                      </span>
                    )}
                    {a.ctaLabel && <span>· CTA: {a.ctaLabel}</span>}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" iconName="Pencil" iconPosition="left" onClick={() => openEditBuilder(a)}>
                      Edit
                    </Button>
                    {a.status !== "published" && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Send"
                        iconPosition="left"
                        loading={publishingId === a.announcementId}
                        onClick={() => handlePublishNow(a)}
                      >
                        Publish Now
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName="Trash2"
                      iconSize={14}
                      onClick={() => setPendingDeleteId(a.announcementId)}
                      className="ml-auto"
                    />
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      <ReauthModal
        open={!!pendingDeleteId}
        label="delete this announcement"
        onConfirm={handleDelete}
        onClose={() => setPendingDeleteId(null)}
      />
    </div>
  )
}

export default AnnouncementsPanel